import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Route } from '../routing/routes';

export function urlParameter(name: string): string | undefined;
export function urlParameter<T>(name: string, deserializer: (param: string) => T): T | undefined;

/**
 * Get a parameter out of a url and optionally transform it in some way
 * @param name
 * @param deserializer
 */
export function urlParameter<T>(
  name: string,
  deserializer?: (param: string) => T,
): T | string | undefined {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(name);
  if (!raw) {
    return;
  }

  if (!deserializer) {
    return raw;
  }

  return deserializer(raw);
}

/**
 * A function for safely redirecting the user to somewhere else locally on the site
 */
export function safeLocalRedirect(url: string): Route | undefined {
  // We don't want anyone to be linked to the login page with a redirect
  // that leads out of the current site which could result in an open
  // redirect vulnerability
  if (!url.startsWith('/')) {
    return;
  }

  return url as Route;
}

export function replaceState(route: Route) {
  window.history.replaceState(null, '', route);
}

/**
 * Uses the URL as the source of truth while
 * making it reactive for re-rendering the caller
 */
export function useUrlParamState<T>(
  opts: UseUrlParamStateOptions<T>,
): [T, (item: T, route: Route) => void] {
  const params = useParams();
  const param = params[opts.name];
  const [state, _setState] = useState<T>(() => {
    if (!param) {
      return opts.defaultValue;
    }
    return opts.deserialize(param) ?? opts.defaultValue;
  });

  const setState = useCallback((item: T, route: Route) => {
    _setState(item);
    replaceState(route);
  }, []);

  return [state, setState];
}

export type UseUrlParamStateOptions<T> = {
  name: string;
  defaultValue: T;
  serialize: (input: T) => string;
  deserialize: (input: string) => T | undefined;
};
